import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  MapPin, Send, Clock, Zap, Wrench, Pencil, Flag, FileText,
} from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAnnonceById, getUrgencyLabel } from "@/hooks/useAnnonces";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { checkMessageContent } from "@/lib/messageFilter";
import { ReportModal } from "@/components/ReportModal";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";
import { getCategoryColors } from "@/lib/categoryColors";
import { cn } from "@/lib/utils";

const AnnonceDetail = () => {
  const { t, locale } = useLanguage("annonces");
  const lNavigate = useLocalizedNavigate();
  const lp = useLocalizedPath();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data: annonce, isLoading } = useAnnonceById(id);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingDevis, setSendingDevis] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const getCategoryLabel = (cat?: string) =>
    cat ? (t(`categories_all.${cat}`, { defaultValue: cat }) || cat) : "";

  useSEO({
    title: annonce
      ? `${annonce.title} — ${getCategoryLabel(annonce.category)} à ${annonce.commune}, ${annonce.wilaya}`
      : t("detail.not_found"),
    description: annonce?.description
      ? annonce.description.slice(0, 155)
      : "Consultez cette demande de travaux et contactez le client directement sur Hirafi.",
    canonical: id ? `/annonce/${id}` : undefined,
    locale,
  });

  const handleContact = async () => {
    if (!user) {
      toast({ title: t("detail.toast_login_title"), description: t("detail.toast_login_desc"), variant: "destructive" });
      lNavigate("/connexion");
      return;
    }
    if (!message.trim() || !annonce) return;

    const blockReason = checkMessageContent(message);
    if (blockReason) {
      toast({
        title: t("detail.toast_blocked_title"),
        description: t("detail.toast_blocked_desc"),
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      const { data: existing } = await supabase
        .from("conversations")
        .select("id")
        .eq("artisan_id", user.id)
        .eq("client_id", annonce.user_id)
        .eq("annonce_id", annonce.id)
        .maybeSingle();

      let convoId = existing?.id;

      if (!convoId) {
        const { data: newConvo, error: convoErr } = await supabase
          .from("conversations")
          .insert({
            artisan_id: user.id,
            client_id: annonce.user_id,
            annonce_id: annonce.id,
          } as any)
          .select("id")
          .single();
        if (convoErr) throw convoErr;
        convoId = newConvo.id;
      }

      const { error: msgErr } = await supabase.from("messages").insert({
        conversation_id: convoId,
        sender_id: user.id,
        content: message.trim(),
        message_type: "text",
      } as any);
      if (msgErr) throw msgErr;

      toast({ title: t("detail.toast_sent_title"), description: t("detail.toast_sent_desc") });
      setMessage("");
      lNavigate("/messagerie");
    } catch (error: any) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleProposeDevis = async () => {
    if (!user || !annonce) return;
    setSendingDevis(true);
    try {
      const { data: existing } = await supabase
        .from("conversations")
        .select("id")
        .eq("artisan_id", user.id)
        .eq("client_id", annonce.user_id)
        .eq("annonce_id", annonce.id)
        .maybeSingle();

      let convoId = existing?.id;

      if (!convoId) {
        const { data: newConvo, error: convoErr } = await supabase
          .from("conversations")
          .insert({
            artisan_id: user.id,
            client_id: annonce.user_id,
            annonce_id: annonce.id,
          } as any)
          .select("id")
          .single();
        if (convoErr) throw convoErr;
        convoId = newConvo.id;
      }

      lNavigate(`/messagerie?conversation=${convoId}&openDevis=1`);
    } catch (error: any) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } finally {
      setSendingDevis(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container max-w-3xl py-8 space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="rounded-xl border bg-card p-6 space-y-4">
            <Skeleton className="h-7 w-3/4" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-20 w-full" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!annonce) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container max-w-3xl py-8 text-center">
          <p className="text-muted-foreground py-12">{t("detail.not_found")}</p>
          <Link to={lp("/annonces")}>
            <Button variant="outline">{t("detail.back")}</Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const catLabel = getCategoryLabel(annonce.category) || annonce.metier || annonce.category;
  const catColors = getCategoryColors(annonce.category ?? "");

  const formatBudget = () => {
    const currency = locale === "ar" ? "دج" : "DA";
    if (annonce.budget_min && annonce.budget_max)
      return `${annonce.budget_min.toLocaleString()} - ${annonce.budget_max.toLocaleString()} ${currency}`;
    if (annonce.budget_min)
      return locale === "ar"
        ? `ابتداءً من ${annonce.budget_min.toLocaleString()} ${currency}`
        : `À partir de ${annonce.budget_min.toLocaleString()} ${currency}`;
    if (annonce.budget_max)
      return locale === "ar"
        ? `حتى ${annonce.budget_max.toLocaleString()} ${currency}`
        : `Jusqu'à ${annonce.budget_max.toLocaleString()} ${currency}`;
    return null;
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container max-w-3xl py-8">
        <div className="mb-4 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <DirectionalArrow className="me-1 h-4 w-4 rotate-180" /> {t("detail.back_simple")}
          </Button>
          {user?.id === annonce.user_id && (
            <Link to={lp(`/modifier-annonce/${annonce.id}`)}>
              <Button variant="outline" size="sm">
                <Pencil className="me-1 h-3.5 w-3.5" /> {t("detail.edit")}
              </Button>
            </Link>
          )}
        </div>

        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Header */}
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={cn("border", catColors.badgeBg, catColors.badgeText)}>
                  <Wrench className="me-1 h-3 w-3" />{catLabel}
                </Badge>
                {annonce.urgency === "asap" && (
                  <Badge className="bg-destructive text-destructive-foreground">
                    <Zap className="me-1 h-3 w-3" />{t("badge.urgent")}
                  </Badge>
                )}
                {annonce.urgency === "thisWeek" && (
                  <Badge variant="warning">{t("badge.this_week")}</Badge>
                )}
                {annonce.urgency === "flexible" && (
                  <Badge variant="outline">{t("urgency.flexible")}</Badge>
                )}
              </div>
              <h1 className="font-display text-2xl font-bold">{annonce.title}</h1>
            </div>

            {/* Info grid */}
            <div className="grid gap-3 sm:grid-cols-3 rounded-lg border bg-muted/50 p-4">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-primary" />
                <span>{annonce.commune}, {annonce.wilaya}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-primary" />
                <span>{getUrgencyLabel(annonce.urgency, locale)}</span>
              </div>
              {formatBudget() && (
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className="font-bold text-xs leading-none text-primary">{locale === "ar" ? "دج" : "DA"}</span>
                  <span>{formatBudget()}</span>
                </div>
              )}
            </div>

            {/* Description */}
            {annonce.description && (
              <div>
                <h2 className="mb-2 font-display text-lg font-semibold">{t("detail.description")}</h2>
                <p className="text-muted-foreground whitespace-pre-line">{annonce.description}</p>
              </div>
            )}

            {/* Photos */}
            {annonce.photos && annonce.photos.length > 0 && (
              <div>
                <h2 className="mb-2 font-display text-lg font-semibold">
                  {t("detail.photos", { count: annonce.photos.length })}
                </h2>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {annonce.photos.map((url, i) => (
                    <img key={i} src={url} alt={`Photo ${i + 1}`} className="aspect-square rounded-lg object-cover border" />
                  ))}
                </div>
              </div>
            )}

            {/* Contact form */}
            {user?.id !== annonce.user_id && (
              <div className="space-y-3 border-t pt-4">
                {user ? (
                  <>
                    <h2 className="font-display text-lg font-semibold">{t("detail.send_message")}</h2>
                    <div className="flex gap-2">
                      <Input
                        placeholder={t("detail.message_placeholder")}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleContact()}
                        className="flex-1"
                      />
                      <Button variant="hero" onClick={handleContact} disabled={sending || !message.trim()}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                    {/* Bouton devis visible pour les artisans seulement */}
                    {user.id !== annonce.user_id && (
                      <Button
                        variant="outline"
                        className="w-full text-sm"
                        onClick={handleProposeDevis}
                        disabled={sendingDevis}
                      >
                        <FileText className="me-2 h-4 w-4" />
                        {sendingDevis ? "Chargement…" : "Proposer un devis"}
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full opacity-70 cursor-not-allowed"
                    onClick={() => lNavigate("/connexion")}
                  >
                    {t("detail.login_to_contact")}
                  </Button>
                )}
              </div>
            )}

            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-muted-foreground">
                {t("detail.published_at", {
                  date: new Date(annonce.published_at || annonce.created_at).toLocaleDateString(
                    locale === "ar" ? "ar-DZ" : "fr-DZ",
                    { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }
                  )
                })}
              </p>
              {user && user.id !== annonce.user_id && (
                <button
                  onClick={() => setReportOpen(true)}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Flag className="h-3 w-3" />
                  {t("detail.report")}
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />

      {annonce && (
        <ReportModal
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          targetType="ad"
          targetId={annonce.id}
          targetName={annonce.title}
        />
      )}
    </div>
  );
};

export default AnnonceDetail;
